import { useEffect, useRef, useState } from "react";
import { api, apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Play, Square, Lock } from "lucide-react";

const haptic = () => navigator.vibrate?.(12);

export const BreathingExercise = ({ accent, unlocked, onComplete }) => {
  const [patterns, setPatterns] = useState(null);
  const [active, setActive] = useState(null);
  const [running, setRunning] = useState(false);
  const [phase, setPhase] = useState({ label: "Ready", scale: 1 });
  const [round, setRound] = useState(0);
  const timers = useRef([]);

  useEffect(() => {
    if (!unlocked) return;
    api.get("/alignment/breathing").then((r) => setPatterns(r.data.patterns)).catch((e) => toast.error(apiErr(e)));
  }, [unlocked]);

  const clearTimers = () => { timers.current.forEach(clearTimeout); timers.current = []; };
  useEffect(() => () => clearTimers(), []);

  const stop = () => { clearTimers(); setRunning(false); setPhase({ label: "Ready", scale: 1 }); setRound(0); };

  const run = (pattern) => {
    clearTimers();
    setActive(pattern);
    setRunning(true);
    setRound(0);
    let t = 0;
    for (let r = 0; r < pattern.rounds; r++) {
      pattern.steps.forEach(([label, secs]) => {
        const scale = label === "Inhale" ? 1.6 : label === "Exhale" ? 1 : label.includes("Hold") ? undefined : 1;
        const at = t;
        timers.current.push(setTimeout(() => {
          haptic();
          setRound(r + 1);
          setPhase((p) => ({ label, scale: scale == null ? p.scale : scale }));
        }, at * 1000));
        t += secs;
      });
    }
    timers.current.push(setTimeout(async () => {
      setRunning(false);
      setPhase({ label: "Complete", scale: 1 });
      toast.success("Breath practice complete.");
      try {
        const { data } = await api.post("/alignment/complete", { kind: "breathing" });
        onComplete?.(data);
      } catch (e) { /* non-blocking */ }
    }, t * 1000));
  };

  if (!unlocked) {
    return (
      <div data-testid="breathing-locked" className="text-center py-10">
        <Lock className="w-10 h-10 mx-auto mb-4 text-slate-500" />
        <p className="text-slate-300 text-sm mb-1">Breathing & meditation are part of Silver and Gold.</p>
        <p className="text-slate-500 text-xs">Upgrade your plan to unlock guided practices.</p>
      </div>
    );
  }
  if (!patterns) return <div className="flex items-center gap-3 text-slate-400 py-8"><Loader2 className="w-5 h-5 animate-spin" /> Loading practices…</div>;

  return (
    <div data-testid="breathing-content">
      <div className="flex flex-col items-center py-4">
        <div
          className="rounded-full flex items-center justify-center transition-transform duration-[3500ms] ease-in-out"
          style={{
            width: 180, height: 180,
            transform: `scale(${phase.scale})`,
            background: `radial-gradient(circle, ${accent}55, ${accent}11)`,
            boxShadow: `0 0 60px ${accent}55`,
          }}
        >
          <span className="font-head font-700 text-lg text-slate-50" data-testid="breathing-phase">{phase.label}</span>
        </div>
        {running && <p className="text-xs text-slate-400 mt-4">Round {round} / {active?.rounds}</p>}
      </div>

      {!running ? (
        <div className="grid sm:grid-cols-3 gap-3 mt-4">
          {patterns.map((p) => (
            <button
              key={p.id}
              data-testid={`breathing-${p.id}`}
              onClick={() => run(p)}
              className="rounded-xl border border-white/10 p-4 text-left hover:border-white/30 transition-colors duration-300"
            >
              <div className="flex items-center gap-2 mb-1" style={{ color: accent }}><Play className="w-4 h-4" /><span className="text-sm font-medium">{p.name}</span></div>
              <p className="text-xs text-slate-400">{p.desc}</p>
            </button>
          ))}
        </div>
      ) : (
        <div className="text-center mt-4">
          <button data-testid="stop-breathing-btn" onClick={stop} className="inline-flex items-center gap-2 rounded-xl border border-white/15 px-5 py-2.5 text-sm text-slate-200 hover:border-white/40 transition-colors duration-300">
            <Square className="w-4 h-4" /> End practice
          </button>
        </div>
      )}
    </div>
  );
};
